import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Lock, ArrowRight, Mail, AlertTriangle } from "lucide-react";
import { getIssue } from "../lib/newsletter";

export default function NewsletterIssue() {
  const { slug } = useParams();
  const [issue, setIssue] = useState(null);
  const [error, setError] = useState("");
  const [unlockEmail, setUnlockEmail] = useState("");
  const [unlocking, setUnlocking] = useState(false);

  const load = (emailOverride) => {
    getIssue(slug, emailOverride)
      .then(setIssue)
      .catch((err) => setError(err.response?.data?.detail || "Could not load issue."));
  };

  useEffect(() => {
    load();
  }, [slug]);

  const tryUnlock = async (e) => {
    e.preventDefault();
    if (!unlockEmail.trim()) return;
    setUnlocking(true);
    try {
      const data = await getIssue(slug, unlockEmail.trim());
      setIssue(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not verify.");
    } finally {
      setUnlocking(false);
    }
  };

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-6 pt-32 pb-24 text-center" data-testid="newsletter-issue-error">
        <AlertTriangle size={48} style={{ color: "#cc7755", margin: "0 auto 1rem" }} />
        <h1 className="newsletter-display text-4xl mb-4">Issue unavailable.</h1>
        <p className="text-base" style={{ color: "var(--ink-muted)" }}>{error}</p>
      </div>
    );
  }

  if (!issue) {
    return <div className="max-w-3xl mx-auto px-6 pt-32 pb-24 text-center" style={{ color: "var(--ink-muted)" }}>Loading briefing…</div>;
  }

  const content = issue.content || {};
  const cover = content.cover_brief;
  const sections = [
    ["market_pulse", "Market Pulse"],
    ["family_office_desk", "Family Office Desk"],
    ["luxury_intelligence", "Luxury Intelligence"],
    ["tax_lien_watch", "Tax Lien Watch"],
    ["private_signals", "Private Signals"],
  ];

  return (
    <div className="newsletter-theme pt-24" data-testid="newsletter-issue-page">
      {/* MASTHEAD */}
      <section className="px-6 py-12" style={{ background: "var(--bg)", borderBottom: "1px solid var(--hairline)" }}>
        <div className="max-w-4xl mx-auto">
          <p className="newsletter-eyebrow mb-3">{issue.month_label} · Private Intelligence</p>
          <h1 className="newsletter-display text-4xl md:text-6xl leading-[0.95] mb-5">
            {issue.headline}
          </h1>
          <p className="newsletter-body text-base md:text-lg" style={{ color: "var(--ink-muted)" }}>
            {issue.subtitle}
          </p>
        </div>
      </section>

      {/* COVER BRIEF */}
      {cover && (
        <article className="px-6 py-14" data-testid="newsletter-cover-brief">
          <div className="max-w-3xl mx-auto">
            <p className="newsletter-eyebrow mb-4">Cover Brief — {cover.title}</p>
            <p className="newsletter-display text-2xl md:text-3xl leading-relaxed" style={{ color: "var(--ink)" }}>
              {cover.thesis}
            </p>
          </div>
        </article>
      )}

      {issue.paywall ? (
        <Paywall issue={issue} email={unlockEmail} setEmail={setUnlockEmail} onUnlock={tryUnlock} unlocking={unlocking} />
      ) : (
        <>
          {sections.map(([key, fallbackTitle]) => {
            const block = content[key];
            if (!block || !block.items) return null;
            return (
              <Section
                key={key}
                kicker={block.title || fallbackTitle}
                items={block.items}
                testid={`newsletter-section-${key}`}
              />
            );
          })}

          {content.closing_note && (
            <section className="px-6 py-16" style={{ background: "var(--bg)" }}>
              <div className="max-w-3xl mx-auto">
                <p className="newsletter-eyebrow mb-5">Closing Note</p>
                <p className="newsletter-display text-2xl md:text-3xl leading-relaxed" style={{ color: "var(--ink)" }}>
                  {content.closing_note}
                </p>
              </div>
            </section>
          )}

          <section className="px-6 py-16">
            <div className="max-w-3xl mx-auto text-center">
              <Mail size={20} style={{ color: "var(--brass)", margin: "0 auto 1rem" }} />
              <p className="newsletter-body text-sm mb-6" style={{ color: "var(--ink-muted)" }}>
                <em>Reply with the topics you want covered next.</em>
              </p>
              <Link to="/newsletter" className="btn-brass" data-testid="newsletter-issue-back">
                Back to the Editorial Desk <ArrowRight size={14} />
              </Link>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function Section({ kicker, items, testid }) {
  return (
    <section className="px-6 py-14" data-testid={testid}>
      <div className="max-w-4xl mx-auto">
        <p className="newsletter-eyebrow mb-7" style={{ color: "var(--brass)" }}>{kicker}</p>
        <div className="space-y-8">
          {items.map((item, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-4 gap-5">
              <div>
                <p className="newsletter-eyebrow text-[10px]" style={{ color: "var(--ink-muted)" }}>{item.kicker}</p>
              </div>
              <div className="md:col-span-3">
                <h3 className="newsletter-display text-2xl mb-2 leading-tight" style={{ color: "var(--ink)" }}>
                  {item.headline}
                </h3>
                <p className="newsletter-body text-base leading-relaxed" style={{ color: "var(--ink-muted)" }}>
                  {item.body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Paywall({ issue, email, setEmail, onUnlock, unlocking }) {
  return (
    <section className="px-6 py-20" style={{ background: "var(--surface)" }} data-testid="newsletter-paywall">
      <div className="max-w-2xl mx-auto text-center">
        <Lock size={36} style={{ color: "var(--brass)", margin: "0 auto 1.5rem" }} />
        <p className="newsletter-eyebrow mb-4">Premium & Advisor Editions</p>
        <h2 className="newsletter-display text-4xl md:text-5xl mb-5 leading-[0.95]">
          The full briefing is <em style={{ color: "var(--brass)" }}>reserved for subscribers</em>.
        </h2>
        <p className="newsletter-body text-base mb-8" style={{ color: "var(--ink-muted)" }}>
          Enter your subscriber email below to unlock <em>Market Pulse · Family Office Desk · Luxury Intelligence ·
          Tax Lien Watch · Private Signals</em>. New here?
          {" "}
          <Link to="/newsletter#subscribe" className="brass-link" data-testid="newsletter-subscribe-cta">
            Subscribe to the briefing
          </Link>.
        </p>
        <form onSubmit={onUnlock} className="flex flex-col md:flex-row gap-3 max-w-lg mx-auto">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="subscriber@example.com"
            className="input-line flex-1"
            data-testid="newsletter-unlock-email"
          />
          <button
            type="submit"
            disabled={unlocking || !email.trim()}
            className="btn-brass"
            style={{ minHeight: 48, justifyContent: "center" }}
            data-testid="newsletter-unlock-submit"
          >
            {unlocking ? "Verifying…" : "Unlock"}
          </button>
        </form>
      </div>
    </section>
  );
}
